
import React, { useState } from 'react';
import { PortfolioItem, KaosSettings } from './types';
import { Icon } from './components/Icon';
import { APP_SETTINGS } from './constants';
import { OrderModal } from './components/OrderModal';

interface ProductKaosProps {
  items: PortfolioItem[];
  settings: KaosSettings;
  onBack: () => void;
  contactNumber?: string;
}

const ProductKaos: React.FC<ProductKaosProps> = ({ items, settings, onBack, contactNumber = APP_SETTINGS.contactWhatsapp }) => {
  const galleryItems = items.filter(item => item.category === 'kaos');
  const [isOrderOpen, setIsOrderOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState('Kaos Custom');

  const handleOrder = (pkgName: string) => {
    setSelectedPackage(pkgName);
    setIsOrderOpen(true);
  };

  const formatPrice = (num: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(num);
  };

  return (
    <div className="min-h-screen bg-[#030712] pt-32 pb-20 text-white">
      <OrderModal 
        isOpen={isOrderOpen} 
        onClose={() => setIsOrderOpen(false)} 
        selectedPackage={selectedPackage} 
        contactNumber={contactNumber}
      />

      <div className="container mx-auto px-6">
        <button 
          onClick={onBack}
          className="flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors group"
        >
          <div className="p-2 glass-morphism rounded-lg group-hover:bg-white/10">
            <Icon name="ArrowRight" className="rotate-180" size={18} />
          </div>
          <span>Kembali ke Beranda</span>
        </button>

        {/* Hero Product */}
        <div className="flex flex-col lg:flex-row items-center gap-12 mb-20">
          <div className="lg:w-1/2">
             <div className="relative aspect-square rounded-[2rem] overflow-hidden border border-white/10 group">
                <img 
                  src="https://images.unsplash.com/photo-1562157873-818bc0726f68?auto=format&fit=crop&q=80&w=800" 
                  alt="Kaos Cotton Premium" 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute top-6 left-6 bg-purple-600 text-white px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider shadow-lg">
                   Premium Quality
                </div>
             </div>
          </div>
          <div className="lg:w-1/2 flex flex-col justify-center">
             <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-morphism mb-6 border border-white/10 w-fit">
                <Icon name="Shirt" size={18} className="text-purple-400" />
                <span className="text-xs font-bold text-slate-300 tracking-wider uppercase">Konveksi Digital</span>
             </div>
             <h1 className="text-4xl md:text-5xl font-extrabold mb-6 leading-tight">Kaos Cotton <br/><span className="gradient-text">High Quality</span></h1>
             <p className="text-slate-400 text-lg mb-8 leading-relaxed">
               Kaos yang dibuat dari bahan tebal pilihan, memberikan kenyamanan maksimal untuk seragam, merchandise, atau koleksi pribadi Anda.
             </p>

             <div className="grid grid-cols-2 gap-4 mb-8">
               <div className="p-4 bg-white/5 rounded-2xl border border-white/10">
                  <div className="text-xs text-slate-500 font-bold uppercase tracking-widest mb-1">Combed 20S (Tebal)</div>
                  <div className="text-2xl font-bold text-white">{formatPrice(settings.basePrice20S)}</div>
                  <div className="text-[10px] text-slate-400">Harga Satuan / Base</div>
               </div>
               <div className="p-4 bg-white/5 rounded-2xl border border-white/10">
                  <div className="text-xs text-slate-500 font-bold uppercase tracking-widest mb-1">Combed 24S (Sedang)</div>
                  <div className="text-2xl font-bold text-white">{formatPrice(settings.basePrice24S)}</div>
                  <div className="text-[10px] text-slate-400">Harga Satuan / Base</div>
               </div>
             </div>
             
             <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-2xl mb-8 flex items-start gap-3">
                <Icon name="Info" className="text-blue-400 mt-1" size={20} />
                <div>
                  <p className="text-sm text-blue-300 font-bold mb-1">Minimal Pemesanan {settings.minOrder} Pcs</p>
                  <p className="text-xs text-slate-400">Untuk menjaga kualitas produksi yang presisi.</p>
                </div>
             </div>

             <button 
                onClick={() => handleOrder('Kaos Cotton Premium')}
                className="w-full py-4 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 rounded-2xl font-bold text-lg text-white shadow-xl shadow-purple-500/20 transition-all flex items-center justify-center gap-2"
             >
                Konsultasi & Order <Icon name="MessageSquare" size={20} />
             </button>
          </div>
        </div>

        {/* Pricing Tables */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-24">
           {/* Paket Harga */}
           <div className="glass-morphism rounded-3xl p-8 border border-white/10">
              <h3 className="text-2xl font-bold mb-6 flex items-center gap-3">
                 <Icon name="Zap" size={24} className="text-green-400" /> Daftar Paket Harga
              </h3>
              <div className="overflow-hidden rounded-xl border border-white/10">
                 <table className="w-full text-left">
                    <thead className="bg-white/5">
                       <tr>
                          <th className="p-4 text-xs font-bold text-slate-400 uppercase tracking-widest">Target Pemesanan</th>
                          <th className="p-4 text-xs font-bold text-white uppercase tracking-widest text-right">Harga / Pcs</th>
                       </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5 text-sm">
                       {settings.tiers.map((tier) => (
                         <tr key={tier.id} className="hover:bg-white/5 transition-colors">
                            <td className="p-4">{tier.label}</td>
                            <td className="p-4 text-right font-bold text-green-400">{formatPrice(tier.price)}</td>
                         </tr>
                       ))}
                       {settings.tiers.length === 0 && (
                         <tr><td colSpan={2} className="p-8 text-center text-slate-600 italic">Belum ada paket harga khusus.</td></tr>
                       )}
                    </tbody>
                 </table>
              </div>
           </div>

           {/* Addons */}
           <div className="glass-morphism rounded-3xl p-8 border border-white/10 flex flex-col justify-center">
               <h3 className="text-2xl font-bold mb-6 flex items-center gap-3">
                 <Icon name="Scissors" size={24} className="text-yellow-400" /> Add-Ons & Custom
              </h3>
              <div className="space-y-4">
                 {settings.addons.map((addon) => (
                   <div key={addon.id} className="p-4 bg-white/5 rounded-2xl flex justify-between items-center hover:bg-white/10 transition-colors">
                      <div className="flex items-center gap-3">
                         <div className="w-8 h-8 rounded-full bg-yellow-500/20 flex items-center justify-center">
                            <span className="text-yellow-400 font-bold text-xs">+</span>
                         </div>
                         <span className="font-medium">{addon.label}</span>
                      </div>
                      <span className="font-bold text-yellow-400">+ {formatPrice(addon.price)} <span className="text-[10px] text-slate-500 font-normal">/pcs</span></span>
                   </div>
                 ))}
                 {settings.addons.length === 0 && (
                   <div className="p-8 text-center text-slate-600 italic bg-white/5 rounded-2xl">Belum ada pilihan add-on.</div>
                 )}
              </div>
              <div className="mt-8 p-4 bg-slate-800/50 rounded-xl text-xs text-slate-400 leading-relaxed border border-white/5">
                 <p><strong>Catatan:</strong> Harga di atas adalah harga estimasi. Spesifikasi sablon khusus (Plastisol, Discharge, Emboss) dapat mengubah harga akhir.</p>
              </div>
           </div>
        </div>

        {/* Gallery Section */}
        <div className="mb-12">
            <h2 className="text-3xl font-bold mb-8 flex items-center gap-3">
               <Icon name="Camera" className="text-purple-500" /> Galeri Hasil Produksi
            </h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
               {galleryItems.length > 0 ? galleryItems.map((item) => (
                  <div key={item.id} className="group relative rounded-2xl overflow-hidden aspect-[4/5] bg-white/5 border border-white/10">
                     <img 
                        src={item.imageUrl || `https://picsum.photos/seed/${item.id}/400/500`} 
                        alt={item.title} 
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                     />
                     <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity p-4 flex flex-col justify-end">
                        <h4 className="font-bold text-white text-sm md:text-base">{item.title}</h4>
                        <p className="text-[10px] text-slate-400 line-clamp-2">{item.description}</p>
                     </div>
                  </div>
               )) : (
                  <div className="col-span-full py-16 text-center border border-dashed border-white/10 rounded-3xl">
                     <Icon name="Camera" className="mx-auto text-slate-600 mb-2" size={32} />
                     <p className="text-slate-500">Belum ada foto galeri produksi yang diunggah.</p>
                  </div>
               )}
            </div>
        </div>

      </div>
    </div>
  );
};

export default ProductKaos;
